'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  Compass,
  Navigation,
  Layers,
  Maximize2,
  ExternalLink,
  MapPin,
  RefreshCw,
  Eye,
  Radio,
  Store,
  Clock,
  ShieldCheck,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Globe
} from 'lucide-react';

export interface CommercialHubData {
  id: string;
  name: string;
  city: string;
  country: string;
  flag: string;
  address: string;
  lat: number;
  lng: number;
  heading: number;
  pitch: number;
  corridor?: string;
  merchantsCount?: string;
  dispatchTime?: string;
  specialty?: string;
  established?: string;
  streetViewUrl?: string;
  streetViewImage?: string;
}

export interface OpenStreetViewerProps {
  hub: CommercialHubData;
  initialMode?: 'streetview' | 'map' | 'satellite';
  height?: string;
  showDetails?: boolean;
  compact?: boolean;
  onExpand?: () => void;
}

// Curated high-res panoramic street-level visuals for major African auto commercial hubs
const HUB_PANORAMA_IMAGES: Record<string, string> = {
  ladipo: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&q=80&w=1600', // Busy market street scene
  abossey_okai: 'https://images.unsplash.com/photo-1570125909232-eb263c188f7e?auto=format&fit=crop&q=80&w=1600', // Commercial auto avenue
  kariakoo: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&q=80&w=1600', // Auto parts trading corridor
  industrial_area: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=1600', // Industrial logistics depot
  suame_magazine: 'https://images.unsplash.com/photo-1504917599217-d4dc5ebe6122?auto=format&fit=crop&q=80&w=1600', // Heavy engineering & parts hub
  apo_mechanic: 'https://images.unsplash.com/photo-1517524008697-84bbe3c3fd98?auto=format&fit=crop&q=80&w=1600', // Mechanic village workshops
};

export default function OpenStreetViewer({
  hub,
  initialMode = 'streetview',
  height = 'h-[440px] sm:h-[520px]',
  showDetails = true,
  compact = false,
  onExpand,
}: OpenStreetViewerProps) {
  const [viewMode, setViewMode] = useState<'streetview' | 'map' | 'satellite'>(initialMode);
  const [yaw, setYaw] = useState(hub.heading || 90);
  const [pitch, setPitch] = useState(hub.pitch || 0);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [useGoogleFallback, setUseGoogleFallback] = useState(true);
  const lastMousePos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Panorama image mapped to this hub
  const panoImage =
    hub.streetViewImage ||
    HUB_PANORAMA_IMAGES[hub.id] ||
    HUB_PANORAMA_IMAGES.ladipo;

  // Drag-to-pan 360° interaction
  const handleMouseDown = (e: React.MouseEvent) => {
    if (viewMode !== 'streetview' || useGoogleFallback) return;
    setIsDragging(true);
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - lastMousePos.current.x;
    const dy = e.clientY - lastMousePos.current.y;
    lastMousePos.current = { x: e.clientX, y: e.clientY };

    setYaw((prev) => (prev - dx * 0.4 + 360) % 360);
    setPitch((prev) => Math.max(-45, Math.min(45, prev + dy * 0.3)));
  };

  const handleMouseUp = () => setIsDragging(false);

  // Touch support for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (viewMode !== 'streetview' || useGoogleFallback) return;
    if (e.touches.length === 1) {
      setIsDragging(true);
      lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - lastMousePos.current.x;
    const dy = e.touches[0].clientY - lastMousePos.current.y;
    lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };

    setYaw((prev) => (prev - dx * 0.5 + 360) % 360);
    setPitch((prev) => Math.max(-45, Math.min(45, prev + dy * 0.4)));
  };

  // OpenStreetMap embed URL with exact pin
  const osmEmbedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${hub.lng - 0.007}%2C${hub.lat - 0.005}%2C${hub.lng + 0.007}%2C${hub.lat + 0.005}&layer=mapnik&marker=${hub.lat}%2C${hub.lng}`;

  // Google Maps Street View / Satellite live embed URL
  const googleStreetViewEmbedUrl = `https://maps.google.com/maps?q=${hub.lat},${hub.lng}&layer=c&cbll=${hub.lat},${hub.lng}&cbp=11,${yaw},0,0,${pitch}&output=svembed`;
  const googleMapEmbedUrl = `https://maps.google.com/maps?q=${hub.lat},${hub.lng}&t=k&z=17&output=embed`;

  // External deep links
  const googleStreetViewDirectLink = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${hub.lat},${hub.lng}&heading=${yaw}&pitch=${pitch}`;
  const googleMapsDirectionsLink = `https://www.google.com/maps/dir/?api=1&destination=${hub.lat},${hub.lng}`;
  const osmDirectLink = `https://www.openstreetmap.org/?mlat=${hub.lat}&mlon=${hub.lng}#map=17/${hub.lat}/${hub.lng}`;
  const mapillaryLink = `https://www.mapillary.com/app/?lat=${hub.lat}&lng=${hub.lng}&z=17&focus=map`;

  return (
    <div className={`w-full bg-[#050B17] rounded-3xl border border-white/10 shadow-2xl overflow-hidden flex flex-col ${compact ? 'rounded-2xl' : ''}`}>
      {/* Top HUD Telemetry Bar */}
      {!compact && (
        <div className="p-3 sm:p-4 bg-white/[0.03] border-b border-white/10 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="text-xl sm:text-2xl shrink-0">{hub.flag}</span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm sm:text-base font-extrabold text-white truncate">
                  {hub.name}
                </h3>
                <span className="text-[10px] font-mono font-bold text-cyan-300 bg-cyan-950/70 border border-cyan-800/60 px-2 py-0.5 rounded-full">
                  {hub.merchantsCount || 'Depot Hub'}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 truncate">
                {hub.corridor || hub.city}
              </p>
            </div>
          </div>

          {/* View Mode Layer Toggles */}
          <div className="flex items-center gap-1 p-1 rounded-full bg-white/10 border border-white/10 text-xs">
            <button
              type="button"
              onClick={() => {
                setViewMode('streetview');
                setUseGoogleFallback(true);
              }}
              className={`px-3 py-1 rounded-full font-bold text-[11px] transition-all flex items-center gap-1 ${
                viewMode === 'streetview'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Compass className="size-3" />
              <span>360° Street View</span>
            </button>

            <button
              type="button"
              onClick={() => setViewMode('map')}
              className={`px-3 py-1 rounded-full font-bold text-[11px] transition-all flex items-center gap-1 ${
                viewMode === 'map'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Layers className="size-3" />
              <span>OSM Street Map</span>
            </button>

            <button
              type="button"
              onClick={() => setViewMode('satellite')}
              className={`px-3 py-1 rounded-full font-bold text-[11px] transition-all flex items-center gap-1 ${
                viewMode === 'satellite'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              <Eye className="size-3" />
              <span>Satellite Aerial</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Viewport */}
      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
        className={`relative w-full ${height} bg-black overflow-hidden select-none cursor-grab active:cursor-grabbing`}
      >
        {viewMode === 'streetview' && !useGoogleFallback ? (
          /* Interactive 360° Panoramic Canvas Street View */
          <div className="relative w-full h-full overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-75 will-change-transform"
              style={{
                backgroundImage: `url(${panoImage})`,
                transform: `scale(${zoom}) translate(${(yaw / 360) * 100 - 50}px, ${pitch * 1.5}px)`,
                filter: 'brightness(1.05) contrast(1.08)',
              }}
            />

            {/* Subtle Vignette & Depth Mask */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40 pointer-events-none" />

            {/* Virtual Road Label Floating HUD */}
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
              <div className="px-4 py-1.5 rounded-full bg-blue-600/80 backdrop-blur-md border border-white/20 text-white text-xs font-bold tracking-wide shadow-xl flex items-center gap-2">
                <Navigation
                  className="size-3.5 text-cyan-300"
                  style={{ transform: `rotate(${yaw}deg)` }}
                />
                <span>{hub.corridor || hub.address}</span>
              </div>
            </div>
          </div>
        ) : viewMode === 'streetview' && useGoogleFallback ? (
          /* Google Street View Live Embed */
          <iframe
            title={`Street View - ${hub.name}`}
            src={googleStreetViewEmbedUrl}
            className="w-full h-full border-0 absolute inset-0"
            allow="fullscreen"
            loading="lazy"
          />
        ) : viewMode === 'map' ? (
          /* OpenStreetMap Live Interactive Tile Layer */
          <iframe
            title={`OpenStreetMap Depot View - ${hub.name}`}
            src={osmEmbedUrl}
            className="w-full h-full border-0 absolute inset-0"
            loading="lazy"
          />
        ) : (
          /* Live Satellite Aerial Layer */
          <iframe
            title={`Satellite Aerial View - ${hub.name}`}
            src={googleMapEmbedUrl}
            className="w-full h-full border-0 absolute inset-0"
            loading="lazy"
          />
        )}

        {/* Live GPS & Heading HUD Overlay */}
        <div className="absolute top-3 left-3 z-10 pointer-events-none">
          <div className="p-2.5 sm:p-3 rounded-2xl bg-black/80 backdrop-blur-md border border-white/15 text-white text-[10px] sm:text-[11px] font-mono space-y-1 shadow-2xl">
            <div className="flex items-center gap-2 text-cyan-300 font-black tracking-wider">
              <Radio className="size-3.5 text-emerald-400 animate-pulse" />
              <span>
                {viewMode === 'streetview'
                  ? useGoogleFallback
                    ? 'GOOGLE STREET VIEW'
                    : '360° STREET TELEMETRY'
                  : viewMode === 'map'
                  ? 'OPENSTREETMAP RADAR'
                  : 'SATELLITE AERIAL'}
              </span>
            </div>
            <div>
              GPS: {hub.lat.toFixed(5)}°N, {hub.lng.toFixed(5)}°E
            </div>
            <div className="text-slate-400">
              Heading: {Math.round(yaw)}° • Dispatch: {hub.dispatchTime || 'Under 45 mins'}
            </div>
          </div>
        </div>

        {/* Interactive Controls Bottom-Right */}
        <div className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 sm:gap-2">
          {viewMode === 'streetview' && !useGoogleFallback && (
            <>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(2, z + 0.2))}
                className="p-2 sm:p-2.5 rounded-full bg-black/80 hover:bg-black text-white border border-white/20 backdrop-blur-md shadow-lg transition-transform active:scale-95"
                title="Zoom In"
              >
                <ZoomIn className="size-3.5 sm:size-4 text-cyan-400" />
              </button>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(1, z - 0.2))}
                className="p-2 sm:p-2.5 rounded-full bg-black/80 hover:bg-black text-white border border-white/20 backdrop-blur-md shadow-lg transition-transform active:scale-95"
                title="Zoom Out"
              >
                <ZoomOut className="size-3.5 sm:size-4 text-cyan-400" />
              </button>
              <button
                type="button"
                onClick={() => setYaw((y) => (y + 90) % 360)}
                className="p-2 sm:p-2.5 rounded-full bg-black/80 hover:bg-black text-white border border-white/20 backdrop-blur-md shadow-lg transition-transform active:scale-95"
                title="Rotate 90°"
              >
                <RotateCw className="size-3.5 sm:size-4 text-cyan-400" />
              </button>
            </>
          )}

          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              className="p-2 sm:p-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white border border-white/20 backdrop-blur-md shadow-lg transition-transform active:scale-95"
              title="Expand Fullscreen 360°"
            >
              <Maximize2 className="size-3.5 sm:size-4" />
            </button>
          )}

          <a
            href={googleStreetViewDirectLink}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full bg-black/80 hover:bg-black text-white text-[10px] sm:text-[11px] font-bold border border-white/20 backdrop-blur-md shadow-lg transition-all flex items-center gap-1"
            title="Open in Google Maps 360°"
          >
            <span>Google 360°</span>
            <ExternalLink className="size-3 text-slate-300" />
          </a>
        </div>
      </div>

      {/* Hub Meta Footer */}
      {showDetails && (
        <div className="p-3 sm:p-4 bg-[#070E1E] border-t border-white/10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-slate-300 space-y-0.5 min-w-0">
            <div className="flex items-center gap-1.5 font-bold text-white truncate">
              <Store className="size-3.5 text-blue-400 shrink-0" />
              <span className="truncate">{hub.address}</span>
            </div>
            <p className="text-[11px] text-slate-400 truncate">
              Specialty: {hub.specialty || 'OEM & Tier-1 Aftermarket spare parts'}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <a
              href={mapillaryLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-[11px] font-semibold transition-colors flex items-center gap-1"
            >
              <span>Mapillary</span>
              <ExternalLink className="size-3" />
            </a>

            <a
              href={googleMapsDirectionsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition-colors flex items-center gap-1.5 shadow-md shadow-blue-600/30"
            >
              <Navigation className="size-3.5" />
              <span>Get Directions</span>
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
